import {getCountryCode, sanitizeFileName} from '@/helpers/shadcn/utils'
import {useAzureBlob} from '@/hooks/useAzureBlob-hook'
import {getFileVerification} from '@/services/fileUploadService'
import {addMetaData, publishProductList} from '@/services/metadata'
import {uploadFileService} from '@/services/uploadFile'
import {NUMBERS} from '@/utils/constants/commonConstants'
import {useState} from 'react'

export const useFileUpload = () => {
  const [isUpdating, setUpdating] = useState(false)
  const [progress, setProgress] = useState(0)
  const [fileUploadError, setFileUploadError] = useState('')
  const {uploadFileOnBlob} = useAzureBlob()
  const onUploadProgress = progressEvent => {
    const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
    setProgress(progress)
  }

  const onUploadFile = (file, onCallback, params = {}) => {
    const formData = new FormData()
    const {fileType, templateType = 'product', adfFlag = true} = params
    formData.append('file', file)
    if (!fileType) {
      setFileUploadError('Upload Error')
      return
    }
    formData.append('fileType', fileType)

    setUpdating(true)
    uploadFileService({formData, onUploadProgress}, templateType, adfFlag)
      .then(response => {
        onCallback(response)
        setUpdating(false)
        setProgress(0)
        setFileUploadError('')
      })
      .catch(error => {
        const errMsg = error?.response?.data?.errorMessage || error?.message || 'An unknown error occurred'
        setUpdating(false)
        setProgress(0)
        setFileUploadError(errMsg)
      })
  }

  const fileVerification = async (file, uploadFor) => {
    const formData = new FormData()
    formData.append('file', file)
    return getFileVerification(formData, uploadFor)
  }
  const uploadFile = async (file, uploadFor) => {
    const formData = new FormData()
    formData.file = file
    return getFileVerification(formData, uploadFor, onUploadProgress)
  }

  const extractUUID = str => {
    const match = str.match(/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/)
    return match ? match[0] : null
  }

  const getFileUploadDetails = async ({file, uploadFor, isTemplateUpload, totalCount, onSuccess, onError}) => {
    try {
      const uploadDate = new Date(Date.now() - 300000) // Sometimes uploadDate is after timestamp so we get an error so we subtract 5 minutes
      const sanitizedFileName = sanitizeFileName(file.name)
      const sanitizedFile = new File([file], sanitizedFileName, {type: file.type})

      const res = await uploadFile(sanitizedFile, uploadFor, isTemplateUpload)

      if (!res || res.success === false || Object.keys(res).length === NUMBERS.Zero) {
        onError?.(res?.message)
        return
      }

      if (!res?.blobUrl) {
        onError?.('Unexpected response from server.')
        return
      }

      // Handle old response structure (blobUrl) - only for non-product/inventory template uploads
      const renamedFile = new File([file], res.fileName, {type: file.type})
      const blobResponse = await uploadFileOnBlob(res, renamedFile)
      if (blobResponse !== true) {
        onError?.('Blob upload failed.')
        return
      }

      const uuid = extractUUID(res.fileName)
      const metadataResponse = await addMetaData({
        fileUploadUUID: uuid,
        applicationId: 1,
        fileName: res?.fileName,
        fileSize: file.size,
        expectedCount: totalCount,
        uploadDate,
        storageContainer: 'input',
        storagePath: res.directory,
        createdBy: 'admin',
        status: 'DRAFT',
        fileType: 'csv',
        templateType: uploadFor,
        countryCode: getCountryCode() ?? 'MX',
        scope: 'pepsi',
      })

      if (metadataResponse?.isSuccess && metadataResponse?.statusCode == 200) {
        await publishProductList?.({
          uuid,
          status: 'published',
          updatedBy: 'user',
          goalConfigId: 1,
        })

        onSuccess?.()
        return
      }
      onError?.('Unexpected response from server.')
    } catch (err) {
      onError?.(`Error verifying columns: ${err.message}`)
    }
  }

  return {
    onUploadFile,
    isUpdating,
    progress,
    fileUploadError,
    setFileUploadError,
    fileVerification,
    uploadFile,
    getFileUploadDetails,
  }
}
